import { zodResolver } from '@hookform/resolvers/zod'
import { useEffect } from 'react'
import { FieldErrors, FormProvider, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'

import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Warehouse } from '@/interfaces/warehouse/Warehouse'
import API from '@/lib/api'
import { extractErrorMessage } from '@/lib/api/errorUtils'
import { components } from '@invo.space/shared'

import { WarehouseForm } from './WarehouseForm'
import { WarehouseFormSchema, WarehouseFormValues } from './schema'

interface WarehouseFormModalProps {
  isOpen: boolean
  onClose: () => void
  warehouse?: Warehouse
  onSuccess?: () => void
}

export const WarehouseFormModal = ({
  isOpen,
  onClose,
  warehouse,
  onSuccess,
}: WarehouseFormModalProps) => {
  const { t } = useTranslation(undefined, {
    keyPrefix: 'warehouse.warehouses',
  })

  const methods = useForm<WarehouseFormValues>({
    resolver: zodResolver(WarehouseFormSchema),
    defaultValues: {
      name: '',
      code: '',
      isActive: true,
      isDefault: false,
    },
  })

  useEffect(() => {
    if (warehouse) {
      methods.setValue('name', warehouse.name)
      methods.setValue('code', warehouse.code)
      methods.setValue('isActive', warehouse.isActive ?? true)
      methods.setValue('isDefault', warehouse.isDefault ?? false)
    } else {
      methods.reset({
        name: '',
        code: '',
        isActive: true,
        isDefault: false,
      })
    }
  }, [warehouse, methods, isOpen])

  const onValid = async (values: WarehouseFormValues) => {
    try {
      if (warehouse?.id) {
        await API.patch<components['schemas']['Warehouse']>(
          `/api/v2/warehouses/${warehouse.id}`,
          {
            name: values.name,
            code: values.code,
            isActive: values.isActive,
            isDefault: values.isDefault,
          },
        )

        toast.success(t('edit.success'))
      } else {
        await API.post<components['schemas']['Warehouse']>(
          '/api/v2/warehouses',
          {
            name: values.name,
            code: values.code,
            isActive: values.isActive,
            isDefault: values.isDefault,
          },
        )

        toast.success(t('new.success'))
      }

      onSuccess?.()
      onClose()
      methods.reset()
    } catch (error: unknown) {
      console.error('Warehouse form error:', error)
      toast.error(warehouse?.id ? t('edit.error') : t('new.error'), {
        description: extractErrorMessage(error) || t('error.description'),
      })
    }
  }

  const onInvalid = (errors: FieldErrors<WarehouseFormValues>) => {
    console.log('Form errors', errors, methods.getValues())
  }

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      onClose()
      methods.reset()
    }
  }

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>
            {warehouse?.id ? t('edit.title') : t('new.title')}
          </DialogTitle>
        </DialogHeader>

        <FormProvider {...methods}>
          <form
            onSubmit={methods.handleSubmit(onValid, onInvalid)}
            className="w-full space-y-6"
          >
            <WarehouseForm autoGenerateCode={!warehouse?.id} />

            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={onClose}>
                {t('form.cancel')}
              </Button>
              <Button type="submit">
                {t('form.save')}
              </Button>
            </div>
          </form>
        </FormProvider>
      </DialogContent>
    </Dialog>
  )
}
